/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { View } from '@components/atoms'
import { Footer } from '@components/systems'
import { MonolithAccountNav } from '@components/systems/monolith/MonolithAccountNav'
import { MonolithOrderList } from '@components/systems/monolith/order/MonolithOrderList'
import { MonolithOrderDetailModal } from '@components/systems/monolith/order/MonolithOrderDetailModal'
import {
	retailMoney,
	retailStatusTitle,
	retailPreorderSteps,
	retailAddress,
	retailPhone,
	retailItemName,
	retailDate,
	downloadRetailOrderDocument,
	downloadRetailOrdersExcel,
} from '@components/systems/monolith/order/retailOrder'
import { customerAccountStyle } from './customerAccount.style'
import { customerOrderListStyle } from './customerOrderList.style'

import { useAppDispatch, useAppSelector, ApiGetDataStatus } from '@redux/index'
import { customerOrderActions } from '@redux/store/index'
import { OrderType } from '@schemas'
import dayjs from 'dayjs'

/**
 * My Order — Monolith(2안).
 *
 * 2026-10-06 Wholesale Orders 와 같은 구조로 바꿨다(사용자 지시):
 *   목록 = Order No. · Date · Status · Amount · Detail (MonolithOrderList) + Download Excel
 *   Detail = `/customer/order/:orderId` — 전체 화면 모달에 주문 한 건 전부(Items · Payment · Recipient ·
 *   Shipping), 맨 아래 Download PDF. 그 전엔 주문마다 밴드를 펼친 채로 늘어놓았다.
 * 금액 계산 · 상태 이름은 옛 페이지 식 그대로(order/retailOrder.ts).
 */
const MonolithCustomerOrderPage = () => {
	const reduxDispatch = useAppDispatch()
	const navigate = useNavigate()
	const { orderId } = useParams<{ orderId: string }>()

	// redux state
	const status: ApiGetDataStatus = useAppSelector(
		state => state.customerOrder.orderListStatus,
	)
	const orderList: OrderType[] = useAppSelector(state => state.customerOrder.orderList)

	// 목록은 /customer/order 진입 때 라우트 사가가 부른다. 자세히 주소로 바로 들어오면 아직 없으니 여기서 부른다
	React.useEffect(() => {
		if (orderId !== undefined && status === 'init') {
			reduxDispatch(customerOrderActions.TRIGGER_INIT_CUSTOMER_ORDER_PAGE(true))
		}
	}, [orderId, status])

	const detailOrder: OrderType | null =
		orderId === undefined
			? null
			: orderList.find(anOrder => anOrder.id === orderId) ?? null

	// 목록을 다 받았는데 그 주문이 없으면(남의 주문 · 없는 번호) 목록으로
	React.useEffect(() => {
		if (orderId === undefined) return
		if (status !== 'ok' && status !== 'empty') return
		if (!orderList.some(anOrder => anOrder.id === orderId)) {
			navigate('/customer/order', { replace: true })
		}
	}, [orderId, orderList, status])

	return (
		<View css={style}>
			<View className="mnWrap">
				<MonolithAccountNav />

				<View className="mnAccountBody mnOrderBody">
					<MonolithOrderList
						label="Orders"
						rows={(status === 'ok' ? orderList : []).map(anOrder => ({
							id: anOrder.id,
							date: retailDate(anOrder.createdAt),
							status: retailStatusTitle(anOrder),
							amount: retailMoney(anOrder.currency, anOrder.paidAmount),
						}))}
						isLoading={status === 'init' || status === 'loading'}
						emptyLabel="No orders yet."
						onDetail={id => navigate(`/customer/order/${encodeURIComponent(id)}`)}
						onDownloadExcel={() => downloadRetailOrdersExcel(orderList)}
					/>
				</View>
			</View>

			{detailOrder !== null && (
				<MonolithOrderDetailModal
					key={detailOrder.id}
					title="Order"
					onClose={() => navigate('/customer/order')}
					onDownloadPdf={() => downloadRetailOrderDocument(detailOrder)}
				>
					<View css={style} className="mnOrderDetailView">
						<CustomerOrderBand order={detailOrder} />
					</View>
				</MonolithOrderDetailModal>
			)}

			<Footer />
		</View>
	)
}

export default MonolithCustomerOrderPage

/** 주문 한 건 — 자세히 모달의 본문. 칸은 옛 My Order 밴드 그대로(customerOrderList.style) */
const CustomerOrderBand = ({ order }: { order: OrderType }) => {
	const money = (value: number) => retailMoney(order.currency, value)
	const preorderSteps = retailPreorderSteps(order)

	return (
		<View className="mnOrder">
			<View className="mnOrderHead">
				<View className="mnOrderHeadLeft">
					<span className="mnMeta">Order No.</span>
					<p className="mnOrderNo">{order.id}</p>
				</View>
				<View className="mnOrderHeadRight">
					<span className="mnMeta mnOrderStatus">{retailStatusTitle(order)}</span>
					<span className="mnMeta">
						{dayjs(order.createdAt).format('MMM DD YYYY · HH:mm')}
					</span>
				</View>
			</View>

			<View className="mnOrderDetail">
				<View className="mnOrderBlock">
					<span className="mnKicker">Items</span>
					<View className="mnOrderItemList">
						{order.detailList.map(anItem => (
							<View key={anItem.id || anItem.productId} className="mnOrderItem">
								<span className="mnOrderItemName">{retailItemName(anItem)}</span>
								<span className="mnOrderItemQty">
									{money(anItem.unitAmount + anItem.optionAmount)} ×{anItem.quantity}
								</span>
							</View>
						))}
					</View>
				</View>

				<View className="mnOrderBlock">
					<span className="mnKicker">Payment</span>
					<View className="mnOrderTable">
						<View className="mnOrderTableRow">
							<span>Subtotal</span>
							<span>{money(order.totalAmount)}</span>
						</View>
						{order.discountAmount > 0 && (
							<View className="mnOrderTableRow">
								<span>{`Coupon${order.couponCode ? ` · ${order.couponCode}` : ''}`}</span>
								<span>{`- ${money(order.discountAmount)}`}</span>
							</View>
						)}
						<View className="mnOrderTableRow">
							<span>Shipping fee</span>
							<span>{money(order.shippingAmount)}</span>
						</View>
						<View className="mnOrderTableRow total">
							<span>{`Total paid (${order.currency})`}</span>
							<span>{money(order.paidAmount)}</span>
						</View>
						{order.refundAmount > 0 && (
							<View className="mnOrderTableRow">
								<span>Refunded</span>
								<span>{`- ${money(order.refundAmount)}`}</span>
							</View>
						)}
					</View>
				</View>

				<View className="mnOrderBlock">
					<span className="mnKicker">Recipient</span>
					<View className="mnOrderTable">
						<View className="mnOrderTableRow">
							<span>Name</span>
							<span>
								{order.receiverGivenName} {order.receiverFamilyName}
							</span>
						</View>
						<View className="mnOrderTableRow">
							<span>Address</span>
							<span>{retailAddress(order)}</span>
						</View>
						<View className="mnOrderTableRow">
							<span>Contact</span>
							<span>{retailPhone(order)}</span>
						</View>
					</View>
				</View>

				{preorderSteps.length > 0 && (
					<View className="mnOrderBlock">
						<span className="mnKicker">Pre-order</span>
						<View className="mnOrderTable">
							{preorderSteps.map(([aLabel, aDate]) => (
								<View key={aLabel} className="mnOrderTableRow">
									<span>{aLabel}</span>
									<span>{aDate}</span>
								</View>
							))}
						</View>
					</View>
				)}

				<View className="mnOrderBlock">
					<span className="mnKicker">Shipping</span>
					<View className="mnOrderTable">
						<View className="mnOrderTableRow">
							<span>Carrier</span>
							<span>
								{order.deliveryCompany === 'local' ? '' : order.deliveryCompany}
							</span>
						</View>
						<View className="mnOrderTableRow">
							<span>Tracking No.</span>
							<span>{order.trackingNumber}</span>
						</View>
						{order.deliveryCompletedAt && (
							<View className="mnOrderTableRow">
								<span>Delivered</span>
								<span>{retailDate(order.deliveryCompletedAt)}</span>
							</View>
						)}
					</View>
				</View>
			</View>
		</View>
	)
}

const style = css({
	...customerAccountStyle,
	...customerOrderListStyle,

	// 자세히 모달 — 밴드의 위 선 · 패딩은 모달 제목 아래에서는 필요 없다
	'.mnOrderDetailView .mnOrder': {
		borderTop: 0,
		paddingTop: 8,
	},
})
